import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import { SERVICES } from "@/lib/business";
import washImg from "@/assets/service-wash-1200.jpg";
import washImg480 from "@/assets/service-wash-480.jpg";
import washImg800 from "@/assets/service-wash-800.jpg";
import washImg1200 from "@/assets/service-wash-1200.jpg";
import washImg480Webp from "@/assets/service-wash-480.webp";
import washImg800Webp from "@/assets/service-wash-800.webp";
import washImg1200Webp from "@/assets/service-wash-1200.webp";
import washImg480Avif from "@/assets/service-wash-480.avif";
import washImg800Avif from "@/assets/service-wash-800.avif";
import washImg1200Avif from "@/assets/service-wash-1200.avif";
import detailImg from "@/assets/service-detail-896.jpg";
import detailImg480 from "@/assets/service-detail-480.jpg";
import detailImg800 from "@/assets/service-detail-800.jpg";
import detailImg896 from "@/assets/service-detail-896.jpg";
import detailImg480Webp from "@/assets/service-detail-480.webp";
import detailImg800Webp from "@/assets/service-detail-800.webp";
import detailImg896Webp from "@/assets/service-detail-896.webp";
import detailImg480Avif from "@/assets/service-detail-480.avif";
import detailImg800Avif from "@/assets/service-detail-800.avif";
import detailImg896Avif from "@/assets/service-detail-896.avif";
import interiorImg from "@/assets/service-interior-1200.jpg";
import interiorImg480 from "@/assets/service-interior-480.jpg";
import interiorImg800 from "@/assets/service-interior-800.jpg";
import interiorImg1200 from "@/assets/service-interior-1200.jpg";
import interiorImg480Webp from "@/assets/service-interior-480.webp";
import interiorImg800Webp from "@/assets/service-interior-800.webp";
import interiorImg1200Webp from "@/assets/service-interior-1200.webp";
import interiorImg480Avif from "@/assets/service-interior-480.avif";
import interiorImg800Avif from "@/assets/service-interior-800.avif";
import interiorImg1200Avif from "@/assets/service-interior-1200.avif";
import ResponsiveImage from "@/components/ResponsiveImage";
import { buildBreadcrumbJsonLd, canonicalLink, pageSocialMeta, SITE_URL } from "@/lib/seo";

const BREADCRUMB_JSONLD = buildBreadcrumbJsonLd([
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
]);

type Variant = { src: string; width: number };
type ImgSet = {
  src: string;
  avif: Variant[];
  webp: Variant[];
  jpg: Variant[];
  width: number;
  height: number;
};

const IMG: Record<string, ImgSet> = {
  basic_wash: {
    src: washImg,
    avif: [
      { src: washImg480Avif, width: 480 },
      { src: washImg800Avif, width: 800 },
      { src: washImg1200Avif, width: 1200 },
    ],
    webp: [
      { src: washImg480Webp, width: 480 },
      { src: washImg800Webp, width: 800 },
      { src: washImg1200Webp, width: 1200 },
    ],
    jpg: [
      { src: washImg480, width: 480 },
      { src: washImg800, width: 800 },
      { src: washImg1200, width: 1200 },
    ],
    width: 1280,
    height: 960,
  },
  full_detail: {
    src: interiorImg,
    avif: [
      { src: interiorImg480Avif, width: 480 },
      { src: interiorImg800Avif, width: 800 },
      { src: interiorImg1200Avif, width: 1200 },
    ],
    webp: [
      { src: interiorImg480Webp, width: 480 },
      { src: interiorImg800Webp, width: 800 },
      { src: interiorImg1200Webp, width: 1200 },
    ],
    jpg: [
      { src: interiorImg480, width: 480 },
      { src: interiorImg800, width: 800 },
      { src: interiorImg1200, width: 1200 },
    ],
    width: 1280,
    height: 960,
  },
  premium_package: {
    src: detailImg,
    avif: [
      { src: detailImg480Avif, width: 480 },
      { src: detailImg800Avif, width: 800 },
      { src: detailImg896Avif, width: 896 },
    ],
    webp: [
      { src: detailImg480Webp, width: 480 },
      { src: detailImg800Webp, width: 800 },
      { src: detailImg896Webp, width: 896 },
    ],
    jpg: [
      { src: detailImg480, width: 480 },
      { src: detailImg800, width: 800 },
      { src: detailImg896, width: 896 },
    ],
    width: 896,
    height: 1200,
  },
};

const SERVICES_JSONLD = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: SERVICES.map((s, i) => ({
    "@type": "ListItem",
    position: i + 1,
    item: {
      "@type": "Service",
      "@id": `${SITE_URL}/services#${s.id}`,
      name: s.name,
      description: s.tagline,
      serviceType: "Mobile Car Detailing",
      provider: { "@id": `${SITE_URL}/#business` },
      areaServed: [
        { "@type": "City", name: "Queens" },
        { "@type": "City", name: "Brooklyn" },
        { "@type": "Place", name: "Long Island" },
      ],
      offers: {
        "@type": "Offer",
        price: String(s.price),
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        url: `${SITE_URL}/book`,
      },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: `${s.name} inclusions`,
        itemListElement: s.features.map((f) => ({
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: f },
        })),
      },
    },
  })),
};

const SERVICES_TITLE = "Mobile Car Detailing Services & Pricing NYC | BLVDS";
const SERVICES_DESCRIPTION = "Mobile car detailing in NYC: Basic Wash $50, Full Detail $120, Premium ceramic coating $250. Queens, Brooklyn & Long Island at your driveway.";

export const Route = createFileRoute("/services")({
  head: () => ({
    meta: [
      { title: SERVICES_TITLE },
      { name: "description", content: SERVICES_DESCRIPTION },
      ...pageSocialMeta("/services", SERVICES_TITLE, SERVICES_DESCRIPTION),
    ],
    links: [canonicalLink("/services")],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(SERVICES_JSONLD),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(BREADCRUMB_JSONLD),
      },
    ],
  }),
  component: ServicesPage,
});

function ServicesPage() {
  return (
    <Layout>
      <section className="pt-16 pb-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-sm font-semibold text-accent uppercase tracking-wider mb-3">Services & Pricing</p>
        <h1 className="text-5xl sm:text-6xl font-bold mb-5">Mobile Car Detailing <span className="text-gradient-primary inline-block pr-2">Services & Pricing</span></h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Pick your package. We handle the rest.
        </p>
      </section>

      <section className="pb-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-6">
          {SERVICES.map((s) => (
            <div
              key={s.id}
              className={`relative rounded-2xl overflow-hidden border transition-smooth ${
                s.highlight
                  ? "border-primary/50 shadow-glow scale-[1.02]"
                  : "border-border hover:border-primary/30"
              }`}
            >
              <div className="relative h-48 overflow-hidden">
                <ResponsiveImage
                  src={IMG[s.id].src}
                  avif={IMG[s.id].avif}
                  webp={IMG[s.id].webp}
                  jpg={IMG[s.id].jpg}
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  alt={s.name}
                  className="w-full h-full object-cover"
                  width={IMG[s.id].width}
                  height={IMG[s.id].height}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
                {s.highlight && (
                  <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-gradient-accent text-accent-foreground text-xs font-bold uppercase tracking-wider">
                    Most Popular
                  </div>
                )}
              </div>

              <div className="p-7 bg-surface">
                <h2 className="text-2xl font-bold mb-1">{s.name}</h2>
                <p className="text-muted-foreground text-sm mb-5">{s.tagline}</p>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-5xl font-bold text-gradient-primary">${s.price}</span>
                </div>
                <p className="text-xs text-muted-foreground mb-6">Approx. {s.duration}</p>
                <ul className="space-y-2.5 mb-7">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/book"
                  search={{ service: s.id } as any}
                  className="group flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-gradient-primary text-primary-foreground font-semibold hover:shadow-glow transition-smooth"
                >
                  Book {s.name}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-smooth" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 p-8 rounded-2xl bg-surface border border-border text-center">
          <h2 className="text-2xl font-bold mb-3">You Name It. We Detail It.</h2>
          <p className="text-muted-foreground mb-5">Fleet detailing, headlight restoration, engine bay wash and more.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-primary/40 text-primary font-semibold hover:bg-primary/10 transition-smooth">
            Get a custom quote
          </Link>
        </div>
      </section>
    </Layout>
  );
}
